import { createContext, useContext, useEffect, useState } from "react";
import { auth, database } from "../constants/firebase";
import {
    ref,
    get,
    onValue,
    onDisconnect,
    serverTimestamp,
    update,
    remove,
} from "firebase/database";
import { signOut } from "firebase/auth";
import useMoveTo from "../constants/navigation";
import { ROUTES } from "../App";
import { clearFcmToken, downgradeUser, saveToken } from "../utils/utils";
import ConfirmationModal from "../components/ConfirmationModal";
import useExchangeRates from "../hooks/useExchangeRates";
import { m } from "framer-motion";

const UserContext = createContext();

export const UserProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const navigate = useMoveTo();
    const { exchangeRates, convertPrice } = useExchangeRates();

    // Listen for native Google login result from WebView
    useEffect(() => {
        const handleMessage = async (event) => {
            try {
                const message = JSON.parse(event.data);

                if (message.type === "DEVICE_ID_RESPONSE") {
                    const { uid, deviceId, sessionId } = message;

                    await Promise.all([
                        remove(ref(database, `Users/${uid}/fcmTokens/${deviceId}`)),
                        remove(ref(database, `Users/${uid}/sessions/${sessionId}`)),
                    ]);

                    signOut(auth)
                        .then(() => {
                            localStorage.removeItem("uid");
                            localStorage.removeItem("sessionId");
                            setCurrentUser(null);
                            navigate(ROUTES.SIGN_IN, { replace: true });
                        })
                        .catch((error) => {
                            console.error("Logout error:", error);
                        });

                }
            } catch { }
        };

        if (window.isInApp) {
            document.addEventListener('message', handleMessage);  // Android
            window.addEventListener('message', handleMessage);  // iOS
            return () => {
                document.removeEventListener("message", handleMessage);
                window.removeEventListener('message', handleMessage);
            };
        }
    }, []);

    const handleLogout = async () => {
        const uid = localStorage.getItem("uid");

        if (window.isInApp) {
            // 📱 Ask React Native for deviceId
            window.ReactNativeWebView.postMessage(
                JSON.stringify({
                    type: "REQUEST_DEVICE_ID_FOR_LOGOUT",
                    uid,
                    sessionId: localStorage.getItem("sessionId"),
                })
            );
            return;
        }

        await clearFcmToken(uid);
        signOut(auth)
            .then(() => {
                localStorage.removeItem("uid");
                localStorage.removeItem("sessionId");
                setCurrentUser(null);
                navigate(ROUTES.SIGN_IN, { replace: true });
            })
            .catch((error) => {
                console.error("Logout error:", error);
            });
    };

    // Modal control functions
    const openLogoutModal = () => setShowLogoutModal(true);
    const closeLogoutModal = () => setShowLogoutModal(false);

    const confirmLogout = () => {
        setShowLogoutModal(false);
        handleLogout();     // 🔥 Logout only on confirm!
    };

    const fetchUserData = async (uid) => {
        const userRef = ref(database, `Users/${uid}`);
        const snapshot = await get(userRef);

        if (!snapshot.exists()) {
            handleLogout();
            return null;
        }

        const userData = snapshot.val();

        if (!userData.email || userData.email.trim() === "") {
            handleLogout();
            return null;
        }

        // ✅ GLOBAL TRIAL CHECK
        await checkTrialExpiration(userData, uid);

        // 🔥 Calculate dynamic progress data
        const progressData = await calculateUserProgress(uid);

        return {
            uid,
            ...userData,
            ...progressData, // inject calculated fields
        };
    };

    const refreshUser = async () => {
        const uid = localStorage.getItem("uid");
        if (uid) {
            const data = await fetchUserData(uid);
            if (data) {
                setCurrentUser(data);
            }
        } else {
            clearFcmToken(uid);
            localStorage.removeItem("uid");
            localStorage.removeItem("sessionId");
            setCurrentUser(null);
            // navigate(ROUTES.SIGN_IN, { replace: true });
        }
    };

    const checkTrialExpiration = async (userData, uid) => {
        if (!userData) return;

        if (userData.subscriptionStatus !== "trial")
            return;

        const now = Date.now();
        const trialEnd = userData.trialEndDate;

        if (!trialEnd) return;

        if (now > trialEnd) {
            console.log("🔥 Trial expired globally");

            await downgradeUser(uid);
            await refreshUser();
        }
    };

    const calculateUserProgress = async (uid) => {
        try {
            let communityStreak = 0;
            let membersJoined = 0;
            let monthlySales = 0;
            let friendsMade = 0;
            let activeDays = 0;
            let highMatchJobs = 0;
            let membersJoinedList = [];
            let friendsMadeList = [];


            const now = new Date();
            const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();

            // -----------------------------
            // 1️⃣ COMMUNITY STREAK + MEMBERS JOINED
            // -----------------------------
            const communitiesSnap = await get(ref(database, "Communities"));
            if (communitiesSnap.exists()) {
                const communities = communitiesSnap.val();

                Object.values(communities).forEach((community) => {
                    if (community.hostId === uid) {
                        // Biggest streak
                        const streak = Number(community.streakCount) || 0;
                        if (streak > communityStreak) {
                            communityStreak = streak;
                        }

                        // Members joined this month
                        if (community.members) {
                            Object.entries(community.members).forEach(([memberId, value]) => {
                                if (value === true && community.memberJoinDates?.[memberId]) {
                                    const joinedAt = new Date(community.memberJoinDates[memberId]).getTime();
                                    if (joinedAt >= startOfMonth) {
                                        membersJoined++;
                                        membersJoinedList.push(memberId); // ✅ store UID
                                    }
                                }
                            });
                        }
                    }
                });
            }

            // -----------------------------
            // 2️⃣ FRIENDS MADE THIS MONTH
            // -----------------------------
            const friendsSnap = await get(ref(database, `Users/${uid}/friends`));
            if (friendsSnap.exists()) {
                const friends = friendsSnap.val();

                Object.keys(friends).forEach((friendId) => {
                    const createdAt = friends[friendId]?.createdAt;
                    if (createdAt && new Date(createdAt).getTime() >= startOfMonth) {
                        friendsMade++;
                        friendsMadeList.push(friendId); // ✅ store UID
                    }
                });
            }

            // -----------------------------
            // 3️⃣ MONTHLY SALES
            // -----------------------------
            const salesSnap = await get(ref(database, `Users/${uid}/salesHistory`));
            if (salesSnap.exists()) {
                const sales = salesSnap.val();

                Object.values(sales).forEach((sale) => {
                    const saleDate = new Date(sale.createdAt).getTime();
                    if (saleDate >= startOfMonth) {
                        monthlySales += Number(sale.saleAmount) || 0;
                    }
                });
            }

            // -----------------------------
            // 4️⃣ ACTIVE DAYS THIS WEEK
            // -----------------------------
            const activitySnap = await get(ref(database, `Users/${uid}/activityLog`));
            if (activitySnap.exists()) {
                const activity = activitySnap.val();

                const now = new Date();
                const startOfWeek = new Date(now);
                startOfWeek.setHours(0, 0, 0, 0); // start of today
                startOfWeek.setDate(now.getDate() - now.getDay()); // start of week (Sunday)

                Object.values(activity).forEach((entry) => {
                    const activityDate = new Date(entry.date); // <-- get .date
                    activityDate.setHours(0, 0, 0, 0); // normalize to start of day

                    if (activityDate >= startOfWeek) {
                        activeDays++;
                    }
                });
            }

            console.log("Active Days This Week:", activeDays);

            // -----------------------------
            // 5️⃣ HIGH MATCH JOBS
            // -----------------------------
            const jobsSnap = await get(ref(database, `Users/${uid}/jobMatches`));
            if (jobsSnap.exists()) {
                const jobs = jobsSnap.val();
                highMatchJobs = Object.keys(jobs).length;
            }

            return {
                communityStreak,
                membersJoined,
                membersJoinedList,
                monthlySales,
                friendsMade,
                friendsMadeList,
                activeDays,
                highMatchJobs,
            };

        } catch (error) {
            console.error("Error calculating progress:", error);
            return {
                communityStreak: 0,
                membersJoined: 0,
                membersJoinedList: [],
                monthlySales: 0,
                friendsMade: 0,
                friendsMadeList: [],
                activeDays: 0,
                highMatchJobs: 0,
            };
        }
    };

    // 🔥 Setup user presence
    const setUserPresence = (uid) => {
        if (!uid) return;

        const userRef = ref(database, `Users/${uid}`);

        const isOnline = {
            online: true,
            lastSeen: serverTimestamp(),
        };

        const isOffline = {
            online: false,
            lastSeen: serverTimestamp(),
        };

        const connectedRef = ref(database, ".info/connected");
        onValue(connectedRef, async (snapshot) => {
            if (snapshot.val() === false) return;

            // 🔥 Log activity once per session (prevents spam)
            const todayKey = new Date().toISOString().split("T")[0];
            // Example: "2026-03-04"

            try {
                await update(
                    ref(database, `Users/${uid}/activityLog/${todayKey}`),
                    {
                        date: new Date().toISOString(),
                    }
                );
            } catch (err) {
                console.error("Activity log error:", err);
            }

            // When client disconnects → mark offline
            onDisconnect(userRef).update(isOffline).then(() => {
                // When client connects → mark online
                update(userRef, isOnline);
            });
        });
    };



    useEffect(() => {
        const uid = localStorage.getItem("uid");

        if (!uid) {
            setCurrentUser(null);
            setLoading(false);
            return;
        }

        let isMounted = true;

        fetchUserData(uid).then((data) => {
            if (!isMounted) return;
            setCurrentUser(data);   // may be null if user not found
            setLoading(false);

            if (data) {
                setUserPresence(uid);
                if (window.isInApp) {
                    window.ReactNativeWebView.postMessage(JSON.stringify({ type: "CURRENT_USER_ID", uid }));
                }
            }
        });

        return () => { isMounted = false };
    }, []);

    // 🔥 Sync FCM token and subscribe to global topic on login/refresh
    useEffect(() => {
        if (currentUser && !loading) {
            const hasSubscribed = sessionStorage.getItem(`fcm_subscribed_${currentUser.uid}`);
            if (!hasSubscribed) {
                saveToken(currentUser).then(() => {
                    sessionStorage.setItem(`fcm_subscribed_${currentUser.uid}`, "true");
                }).catch(err => console.error("FCM sync error:", err));
            }
        }
    }, [currentUser, loading]);



    const value = {
        currentUser,
        isAuthenticated: !!currentUser,
        loading,
        refreshUser,
        isHost: currentUser?.role === 'Host',
        isFree: currentUser?.role === 'Free',
        subscriptionStatus: currentUser?.subscriptionStatus || 'none',
        openLogoutModal,
        closeLogoutModal,
        confirmLogout,
    };

    return (
        <UserContext.Provider value={value}>
            {children}

            {/* 🔥 Logout Confirmation Modal */}
            <ConfirmationModal
                isOpen={showLogoutModal}
                onClose={closeLogoutModal}
                onConfirm={confirmLogout}
                message="Are you sure you want to logout?"
            />
        </UserContext.Provider>
    );
};

export const useUser = () => useContext(UserContext);


const CommunitiesContext = createContext();

export const useCommunities = () => useContext(CommunitiesContext);

export const CommunitiesProvider = ({ children }) => {
    const [communities, setCommunities] = useState([]);
    const [loading, setLoading] = useState(true);

    return (
        <CommunitiesContext.Provider value={{ communities, setCommunities, loading, setLoading }}>
            {children}
        </CommunitiesContext.Provider>
    );
};